import { useGraphqlQuery } from "./useGraphqlQuery"
import { Query, RoleEnum, Viewer } from "common/types/graphql"
import { gql } from "common/lib/gql"
import { createContext, useContext, useSyncExternalStore } from "react"
import { hasActiveSession } from "common/lib/cookies"

const VIEWER_QUERY = gql`
  query {
    viewer {
      role
      user {
        id
        name
        avatar
        commentNotificationCount
        allow {
          analytics
          moderateComments
          videoWithMetadata
        }
        newsFeedCount
        donations {
          lastDonated
          lastRegionDonatedTo
          lastDonationRecurring
          numDonations
          numDonationsThisYear
          totalAmount
          totalAmountThisYear
        }
      }
    }
  }
`

export const TestViewerContext = createContext<Pick<Viewer, "role" | "user"> | undefined>(undefined)

// The API returns this for viewers with no session, so anonymous users see
// the same shape whether or not we skip the request. The reference is stable
// so effects that depend on `viewer` don't re-run on every parent render.
const ANONYMOUS_VIEWER = { role: RoleEnum.Reader, user: null } as Viewer

// Cookies have no change notification, and we only need the value once
// per page load.
const subscribeToSession = () => () => {}

/**
 * Data hook to return the current viewer.
 * This hook also implements helper attributes to determine viewer roles.
 */
export const useViewer = () => {
  // `hasActiveSession` reads `document.cookie`, which the server doesn't have.
  // Returning null from the server snapshot keeps the first client render
  // identical to the server render, so signed-in users don't trip a hydration
  // mismatch.
  const sessionActive = useSyncExternalStore<boolean | null>(
    subscribeToSession,
    hasActiveSession,
    () => null,
  )

  const { data, loaded: queryLoaded } = useGraphqlQuery<Query>(
    sessionActive === true ? VIEWER_QUERY : null,
  )

  const testViewer = useContext(TestViewerContext)
  const anonymousViewer = sessionActive === false ? ANONYMOUS_VIEWER : undefined
  const viewer = testViewer || data?.viewer || anonymousViewer

  return {
    viewer,
    loaded: sessionActive === false || queryLoaded,
    isSignedIn: !!viewer?.user,
    isEditor: viewer?.role === RoleEnum.Editor,
    isAuthor: viewer?.role === RoleEnum.Author,
    isReader: viewer?.role === RoleEnum.Reader,
  }
}
